import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Phone,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Star,
  Settings,
  Volume2,
  Clock,
  ArrowRightLeft,
  Mail,
  MessageSquare,
  Users,
  RefreshCw,
  Check,
  Globe,
  PhoneOutgoing,
  Info,
  Wrench,
  Play,
  Database,
  Eye,
  Filter,
  Terminal,
  X,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Label,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@/components/ui'
import { inboundApi, type CreateInboundNumberData, type UpdateInboundNumberData, type TransferConfig } from '@/api/endpoints/inbound'
import type { InboundNumber } from '@/types'
import { cn } from '@/lib/utils'
import { VoiceSelector } from '@/components/common/VoiceSelector'

// Default voice ID (same as outbound calls for consistent caller experience)
const DEFAULT_VOICE_ID = '2f9fdbc7-4bf2-4792-8a18-21ce3c93978f'

const MODELS = [
  { value: 'enhanced', label: 'Enhanced' },
  { value: 'base', label: 'Base' },
  { value: 'turbo', label: 'Turbo' },
]

const LANGUAGES = [
  { value: 'babel', label: 'Babel (Auto-detect)' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
]

const PROMPT_TYPES = [
  { value: 'inbound_prompt_advisor', label: 'Inbound with Advisor (Full)' },
  { value: 'inbound_prompt', label: 'Inbound Basic (No Tools)' },
]

const formatPhoneNumber = (phone: string) => {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

interface FormData extends Omit<CreateInboundNumberData, 'transfer_config'> {
  transfer_default: string
  transfer_pricing: string
  transfer_technical: string
  business_hours_start: number
  business_hours_end: number
  business_hours_timezone: string
}

export function InboundNumbers() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('numbers')
  const [activeOnly, setActiveOnly] = useState(false)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false)
  const [isBlandStatusDialogOpen, setIsBlandStatusDialogOpen] = useState(false)
  const [selectedNumber, setSelectedNumber] = useState<InboundNumber | null>(null)
  const [blandStatusData, setBlandStatusData] = useState<unknown>(null)
  const [configLog, setConfigLog] = useState<string[]>([])
  const [selectedConfigNumber, setSelectedConfigNumber] = useState('')

  const initialFormData: FormData = {
    phone_number: '',
    name: '',
    description: '',
    is_active: true,
    is_primary: false,
    voice: DEFAULT_VOICE_ID,
    model: 'base',
    language: 'babel',
    max_duration: 10,
    prompt_type: 'inbound_prompt_advisor',
    transfer_default: '',
    transfer_pricing: '',
    transfer_technical: '',
    enable_advisor_tool: true,
    enable_caller_context_tool: true,
    enable_email_tool: true,
    email_cc: '',
    email_bcc: '',
    transfer_notify_email: '',
    business_hours_start: 8,
    business_hours_end: 17,
    business_hours_timezone: 'America/Los_Angeles',
  }

  const [formData, setFormData] = useState<FormData>(initialFormData)

  // Fetch inbound numbers
  const { data, isLoading, error } = useQuery({
    queryKey: ['inbound-numbers', activeOnly],
    queryFn: () => inboundApi.getAll(activeOnly),
  })

  const numbers = data?.data?.items || []

  // Fetch Bland.ai tools
  const { data: toolsData, isLoading: toolsLoading } = useQuery({
    queryKey: ['bland-tools'],
    queryFn: () => inboundApi.listBlandTools(),
    enabled: activeTab === 'tools',
  })

  const tools = toolsData?.data?.tools || []

  // Fetch available voices from Bland.ai
  const { data: voicesData } = useQuery({
    queryKey: ['bland-voices'],
    queryFn: () => inboundApi.listVoices(),
  })

  // Format voices for dropdown - handle various Bland.ai response formats
  const voices = (voicesData?.data?.voices || []).map((v: Record<string, unknown>) => ({
    value: (v.voice_id || v.id || '') as string,
    label: (v.name || v.voice_id || v.id || 'Unknown') as string,
    description: v.description as string | undefined,
  })).filter((v: { value: string }) => v.value)

  // Helper to get voice display name
  const getVoiceName = (voiceId: string) => {
    const voice = voices.find((v: { value: string }) => v.value === voiceId)
    if (voice) return voice.label
    // Show shortened ID if not found in list
    if (voiceId && voiceId.length > 12) return `${voiceId.slice(0, 8)}...`
    return voiceId || 'Default'
  }

  // Convert form data to API format
  const formToApiData = (form: FormData): CreateInboundNumberData | UpdateInboundNumberData => {
    const transferConfig: TransferConfig = {}
    if (form.transfer_default) transferConfig.default = form.transfer_default
    if (form.transfer_pricing) transferConfig.pricing = form.transfer_pricing
    if (form.transfer_technical) transferConfig.technical = form.transfer_technical

    return {
      phone_number: form.phone_number,
      name: form.name,
      description: form.description,
      is_active: form.is_active,
      is_primary: form.is_primary,
      voice: form.voice,
      model: form.model,
      language: form.language,
      max_duration: form.max_duration,
      prompt_type: form.prompt_type,
      transfer_config: Object.keys(transferConfig).length > 0 ? transferConfig : undefined,
      enable_advisor_tool: form.enable_advisor_tool,
      enable_caller_context_tool: form.enable_caller_context_tool,
      enable_email_tool: form.enable_email_tool,
      email_cc: form.email_cc,
      email_bcc: form.email_bcc,
      transfer_notify_email: form.transfer_notify_email,
      business_hours_start: form.business_hours_start,
      business_hours_end: form.business_hours_end,
      business_hours_timezone: form.business_hours_timezone,
    }
  }

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateInboundNumberData) => inboundApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbound-numbers'] })
      setIsAddDialogOpen(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInboundNumberData }) => inboundApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbound-numbers'] })
      setIsEditDialogOpen(false)
      setIsSettingsDialogOpen(false)
      setSelectedNumber(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => inboundApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbound-numbers'] })
      setIsDeleteDialogOpen(false)
      setSelectedNumber(null)
    },
  })

  const setPrimaryMutation = useMutation({
    mutationFn: (id: string) => inboundApi.setPrimary(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbound-numbers'] })
    },
  })

  const syncMutation = useMutation({
    mutationFn: (id: string) => inboundApi.syncWithBland(id),
    onSuccess: (_, phoneNumber) => {
      queryClient.invalidateQueries({ queryKey: ['inbound-numbers'] })
      addConfigLog(`Configured ${phoneNumber} on Bland.ai`)
    },
  })

  const configureAllMutation = useMutation({
    mutationFn: () => inboundApi.configureAll(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['inbound-numbers'] })
      addConfigLog('Configure All completed')
      addConfigLog(JSON.stringify(response.data, null, 2))
    },
    onError: (error) => {
      addConfigLog(`Error: ${error}`)
    },
  })

  const configureLegacyMutation = useMutation({
    mutationFn: () => inboundApi.configureLegacy(),
    onSuccess: (response) => {
      addConfigLog('Legacy configuration completed')
      addConfigLog(JSON.stringify(response.data, null, 2))
    },
    onError: (error) => {
      addConfigLog(`Error: ${error}`)
    },
  })

  const seedDefaultMutation = useMutation({
    mutationFn: () => inboundApi.seedDefault(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['inbound-numbers'] })
      addConfigLog(response.data.message || 'Seed completed')
    },
    onError: (error) => {
      addConfigLog(`Error: ${error}`)
    },
  })

  // Call Settings
  const [outboundCallerId, setOutboundCallerId] = useState('')
  const [outboundVoice, setOutboundVoice] = useState('')
  const [outboundSettingsSaved, setOutboundSettingsSaved] = useState(false)

  const { data: callSettingsData } = useQuery({
    queryKey: ['call-settings'],
    queryFn: () => inboundApi.getCallSettings(),
  })

  useEffect(() => {
    if (callSettingsData?.data?.outbound_caller_id !== undefined) {
      setOutboundCallerId(callSettingsData.data.outbound_caller_id)
    }
    if (callSettingsData?.data?.outbound_voice !== undefined) {
      setOutboundVoice(callSettingsData.data.outbound_voice)
    }
  }, [callSettingsData])

  const updateCallSettingsMutation = useMutation({
    mutationFn: (settings: { outbound_caller_id?: string; outbound_voice?: string }) =>
      inboundApi.updateCallSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-settings'] })
      setOutboundSettingsSaved(true)
      setTimeout(() => setOutboundSettingsSaved(false), 3000)
    },
  })

  const handleSaveOutboundSettings = () => {
    updateCallSettingsMutation.mutate({
      outbound_caller_id: outboundCallerId,
      outbound_voice: outboundVoice,
    })
  }

  const getEffectiveCallerIdDisplay = () => {
    if (outboundCallerId.toLowerCase() === 'random') {
      return 'Random (Bland.ai pool - callbacks will NOT reach AI)'
    }
    if (outboundCallerId) {
      return formatPhoneNumber(outboundCallerId)
    }
    if (callSettingsData?.data?.inbound_number) {
      return `${formatPhoneNumber(callSettingsData.data.inbound_number)} (inbound number)`
    }
    return 'Random (no inbound configured)'
  }

  const addConfigLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString()
    setConfigLog(prev => [...prev, `[${timestamp}] ${message}`])
  }

  const resetForm = () => {
    setFormData(initialFormData)
  }

  const handleAddNumber = () => {
    createMutation.mutate(formToApiData(formData) as CreateInboundNumberData)
  }

  const handleEditNumber = () => {
    if (!selectedNumber) return
    updateMutation.mutate({
      id: selectedNumber.phone_number,
      data: formToApiData(formData) as UpdateInboundNumberData,
    })
  }

  const handleDeleteNumber = () => {
    if (!selectedNumber) return
    deleteMutation.mutate(selectedNumber.phone_number)
  }

  const handleViewBlandStatus = async (phoneNumber: string) => {
    try {
      const response = await inboundApi.getBlandStatus(phoneNumber)
      setBlandStatusData(response.data)
      setIsBlandStatusDialogOpen(true)
    } catch (error) {
      console.error('Failed to fetch Bland status:', error)
    }
  }

  const handleConfigureSingle = () => {
    if (!selectedConfigNumber) return
    syncMutation.mutate(selectedConfigNumber)
  }

  const openEditDialog = (number: InboundNumber) => {
    setSelectedNumber(number)
    setFormData({
      phone_number: number.phone_number,
      name: number.name || '',
      description: number.description || '',
      is_active: number.is_active,
      is_primary: number.is_primary,
      voice: number.voice,
      model: number.model,
      language: number.language || 'babel',
      max_duration: number.max_duration,
      prompt_type: number.prompt_type || 'inbound_prompt_advisor',
      transfer_default: number.transfer_config?.default || '',
      transfer_pricing: number.transfer_config?.pricing || '',
      transfer_technical: number.transfer_config?.technical || '',
      enable_advisor_tool: number.enable_advisor_tool ?? number.use_advisor_tool ?? true,
      enable_caller_context_tool: number.enable_caller_context_tool ?? number.use_caller_context_tool ?? true,
      enable_email_tool: number.enable_email_tool ?? number.use_email_tool ?? true,
      email_cc: number.email_cc || '',
      email_bcc: number.email_bcc || '',
      transfer_notify_email: number.transfer_notify_email || '',
      business_hours_start: number.business_hours_start ?? 8,
      business_hours_end: number.business_hours_end ?? 17,
      business_hours_timezone: number.business_hours_timezone || 'America/Los_Angeles',
    })
    setIsEditDialogOpen(true)
  }

  const openSettingsDialog = (number: InboundNumber) => {
    setSelectedNumber(number)
    setFormData({
      phone_number: number.phone_number,
      name: number.name || '',
      description: number.description || '',
      is_active: number.is_active,
      is_primary: number.is_primary,
      voice: number.voice,
      model: number.model,
      language: number.language || 'babel',
      max_duration: number.max_duration,
      prompt_type: number.prompt_type || 'inbound_prompt_advisor',
      transfer_default: number.transfer_config?.default || '',
      transfer_pricing: number.transfer_config?.pricing || '',
      transfer_technical: number.transfer_config?.technical || '',
      enable_advisor_tool: number.enable_advisor_tool ?? number.use_advisor_tool ?? true,
      enable_caller_context_tool: number.enable_caller_context_tool ?? number.use_caller_context_tool ?? true,
      enable_email_tool: number.enable_email_tool ?? number.use_email_tool ?? true,
      email_cc: number.email_cc || '',
      email_bcc: number.email_bcc || '',
      transfer_notify_email: number.transfer_notify_email || '',
      business_hours_start: number.business_hours_start ?? 8,
      business_hours_end: number.business_hours_end ?? 17,
      business_hours_timezone: number.business_hours_timezone || 'America/Los_Angeles',
    })
    setIsSettingsDialogOpen(true)
  }

  const openDeleteDialog = (number: InboundNumber) => {
    setSelectedNumber(number)
    setIsDeleteDialogOpen(true)
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Failed to load inbound numbers. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Inbound Numbers</h1>
          <p className="text-muted-foreground">
            Manage phone numbers for AI-powered inbound calls
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => seedDefaultMutation.mutate()} disabled={seedDefaultMutation.isPending}>
            <Database className="h-4 w-4 mr-2" />
            Seed from Env
          </Button>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Number
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Phone className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{numbers.length}</p>
                <p className="text-sm text-muted-foreground">Total Numbers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{numbers.filter(n => n.is_active).length}</p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{numbers.filter(n => n.is_primary).length}</p>
                <p className="text-sm text-muted-foreground">Primary</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{numbers.filter(n => n.enable_advisor_tool || n.use_advisor_tool).length}</p>
                <p className="text-sm text-muted-foreground">With Advisor</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="numbers">
            <Phone className="h-4 w-4 mr-2" />
            All Numbers
          </TabsTrigger>
          <TabsTrigger value="tools">
            <Wrench className="h-4 w-4 mr-2" />
            Bland.ai Tools
          </TabsTrigger>
          <TabsTrigger value="configure">
            <Settings className="h-4 w-4 mr-2" />
            Configure
          </TabsTrigger>
        </TabsList>

        {/* Numbers Tab */}
        <TabsContent value="numbers" className="space-y-4">
          {/* Outbound Call Settings */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <PhoneOutgoing className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Outbound Call Settings</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Caller ID */}
              <div className="space-y-2">
                <Label htmlFor="outbound-caller-id">Outbound Caller ID</Label>
                <Input
                  id="outbound-caller-id"
                  placeholder="Leave empty to use inbound number"
                  value={outboundCallerId}
                  onChange={(e) => setOutboundCallerId(e.target.value)}
                  className="max-w-sm"
                />
                <p className="text-xs text-muted-foreground">
                  The phone number shown on outbound calls. Leave empty to use the inbound number (recommended for callbacks). Enter "random" to use Bland.ai's random pool.
                </p>
              </div>

              {/* Outbound Voice */}
              <div className="space-y-2">
                <VoiceSelector
                  value={outboundVoice}
                  onChange={setOutboundVoice}
                  label="Outbound Voice"
                  showPreview={true}
                  placeholder="Select voice for outbound calls..."
                />
                <p className="text-xs text-muted-foreground">
                  The AI voice used for outbound sales calls to leads.
                </p>
              </div>

              {/* Save Button and Info */}
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between pt-2 border-t">
                <Button
                  onClick={handleSaveOutboundSettings}
                  disabled={updateCallSettingsMutation.isPending}
                  variant={outboundSettingsSaved ? 'outline' : 'default'}
                >
                  {updateCallSettingsMutation.isPending ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : outboundSettingsSaved ? (
                    <Check className="h-4 w-4 mr-2" />
                  ) : null}
                  {outboundSettingsSaved ? 'Saved' : 'Save Outbound Settings'}
                </Button>
                <div className="flex-shrink-0 p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Effective caller ID:</span>
                    <span className="font-medium">{getEffectiveCallerIdDisplay()}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filter */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Switch
                id="active-only"
                checked={activeOnly}
                onCheckedChange={setActiveOnly}
              />
              <Label htmlFor="active-only" className="text-sm">Active Only</Label>
            </div>
            <Button variant="ghost" size="sm" onClick={() => queryClient.invalidateQueries({ queryKey: ['inbound-numbers'] })}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Numbers Grid */}
          {isLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="pt-6">
                    <div className="h-24 bg-muted rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : numbers.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <Phone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium text-lg">No inbound numbers configured</h3>
                  <p className="text-muted-foreground mb-4">
                    Add your first phone number to start receiving AI-powered calls
                  </p>
                  <Button onClick={() => setIsAddDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Number
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {numbers.map(number => (
                <Card key={number.phone_number} className={cn(
                  'relative transition-shadow hover:shadow-md',
                  number.is_primary && 'ring-2 ring-yellow-400'
                )}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          'p-2 rounded-lg',
                          number.is_active ? 'bg-green-100' : 'bg-gray-100'
                        )}>
                          <Phone className={cn(
                            'h-5 w-5',
                            number.is_active ? 'text-green-600' : 'text-gray-400'
                          )} />
                        </div>
                        <div>
                          <CardTitle className="text-base">
                            {formatPhoneNumber(number.phone_number)}
                          </CardTitle>
                          {number.name && (
                            <p className="text-sm text-muted-foreground">{number.name}</p>
                          )}
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(number)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openSettingsDialog(number)}>
                            <Settings className="h-4 w-4 mr-2" />
                            Settings
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleViewBlandStatus(number.phone_number)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Bland Status
                          </DropdownMenuItem>
                          {!number.is_primary && (
                            <DropdownMenuItem onClick={() => setPrimaryMutation.mutate(number.phone_number)}>
                              <Star className="h-4 w-4 mr-2" />
                              Set as Primary
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => syncMutation.mutate(number.phone_number)}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Sync with Bland
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => openDeleteDialog(number)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Description */}
                    {number.description && (
                      <p className="text-sm text-muted-foreground mb-3">{number.description}</p>
                    )}

                    {/* Status Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {number.is_primary && (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          Primary
                        </Badge>
                      )}
                      <Badge variant={number.is_active ? 'default' : 'secondary'}>
                        {number.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    {/* Settings Summary */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Volume2 className="h-3 w-3" />
                        <span>{getVoiceName(number.voice)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{number.max_duration}m max</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Globe className="h-3 w-3" />
                        <span>{number.language || 'babel'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Settings className="h-3 w-3" />
                        <span className="capitalize">{number.model}</span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="flex gap-2 mt-4 pt-4 border-t flex-wrap">
                      {(number.enable_advisor_tool || number.use_advisor_tool) && (
                        <Badge variant="outline" className="text-xs">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          Advisor
                        </Badge>
                      )}
                      {(number.enable_caller_context_tool || number.use_caller_context_tool) && (
                        <Badge variant="outline" className="text-xs">
                          <Users className="h-3 w-3 mr-1" />
                          Context
                        </Badge>
                      )}
                      {(number.enable_email_tool || number.use_email_tool) && (
                        <Badge variant="outline" className="text-xs">
                          <Mail className="h-3 w-3 mr-1" />
                          Email
                        </Badge>
                      )}
                      {number.transfer_config?.default && (
                        <Badge variant="outline" className="text-xs">
                          <ArrowRightLeft className="h-3 w-3 mr-1" />
                          Transfer
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tools Tab */}
        <TabsContent value="tools" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Bland.ai Custom Tools</CardTitle>
                <Button variant="outline" size="sm" onClick={() => queryClient.invalidateQueries({ queryKey: ['bland-tools'] })}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                These are the custom tools configured in Bland.ai for inbound call handling.
              </p>
            </CardHeader>
          </Card>

          {toolsLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="pt-6">
                    <div className="h-16 bg-muted rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : tools.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No custom tools found in Bland.ai</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {(tools as Array<{ tool_id?: string; id?: string; name?: string; description?: string }>).map((tool, index) => (
                <Card key={tool.tool_id || tool.id || index}>
                  <CardContent className="pt-6">
                    <h3 className="font-semibold mb-2">{tool.name || 'Unnamed Tool'}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{tool.description || 'No description'}</p>
                    <p className="text-xs text-muted-foreground">ID: {tool.tool_id || tool.id}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Configure Tab */}
        <TabsContent value="configure" className="space-y-4">
          {/* Outbound Call Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Outbound Call Settings</CardTitle>
              <p className="text-sm text-muted-foreground">
                Configure the caller ID and voice used for outbound calls to leads.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Outbound Caller ID</Label>
                <Input
                  placeholder="Leave empty to use inbound number, or 'random' for Bland pool"
                  value={outboundCallerId}
                  onChange={(e) => setOutboundCallerId(e.target.value)}
                  className="max-w-md"
                />
                <p className="text-xs text-muted-foreground">
                  Empty = Use primary inbound number (callbacks reach AI) | Phone number = Use that number | "random" = Bland.ai pool (callbacks won't reach AI)
                </p>
              </div>

              <VoiceSelector
                value={outboundVoice}
                onChange={setOutboundVoice}
                label="Outbound Voice"
                showPreview={true}
                placeholder="Select voice for outbound calls..."
              />

              <div className="flex items-center gap-4">
                <Button onClick={handleSaveOutboundSettings} disabled={updateCallSettingsMutation.isPending}>
                  {updateCallSettingsMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                  {outboundSettingsSaved ? 'Saved' : 'Save Settings'}
                </Button>
                <div className="text-sm">
                  <span className="text-muted-foreground">Effective caller ID: </span>
                  <span className="font-mono">{getEffectiveCallerIdDisplay()}</span>
                </div>
              </div>

              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                Note: Changes are runtime only. To persist permanently, update environment variables in Cloud Run.
              </div>
            </CardContent>
          </Card>

          {/* Configure Inbound Numbers */}
          <Card>
            <CardHeader>
              <CardTitle>Configure Inbound Numbers</CardTitle>
              <p className="text-sm text-muted-foreground">
                Apply database configurations to Bland.ai. This will create/update tools and configure each phone number.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Configure All */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-2">Configure All Active Numbers</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Configures all active phone numbers from the database on Bland.ai.
                </p>
                <Button onClick={() => { setConfigLog([]); configureAllMutation.mutate() }} disabled={configureAllMutation.isPending}>
                  {configureAllMutation.isPending ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  Configure All
                </Button>
              </div>

              {/* Configure Single */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-2">Configure Single Number</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Configure a specific phone number on Bland.ai.
                </p>
                <div className="flex gap-4">
                  <Select value={selectedConfigNumber} onValueChange={setSelectedConfigNumber}>
                    <SelectTrigger className="max-w-xs">
                      <SelectValue placeholder="Select a number..." />
                    </SelectTrigger>
                    <SelectContent>
                      {numbers.map(num => (
                        <SelectItem key={num.phone_number} value={num.phone_number}>
                          {num.name} ({num.phone_number})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleConfigureSingle} disabled={!selectedConfigNumber || syncMutation.isPending}>
                    {syncMutation.isPending ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                    Configure
                  </Button>
                </div>
              </div>

              {/* Legacy Configure */}
              <div className="border rounded-lg p-4 bg-muted/50">
                <h3 className="font-medium mb-2">Legacy Configuration</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Use the original configure method with BLAND_INBOUND_NUMBER environment variable. This bypasses database configuration.
                </p>
                <Button variant="secondary" onClick={() => { setConfigLog([]); configureLegacyMutation.mutate() }} disabled={configureLegacyMutation.isPending}>
                  {configureLegacyMutation.isPending ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  Configure Legacy
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Configuration Log */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Terminal className="h-5 w-5" />
                  Configuration Log
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setConfigLog([])}>
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-900 text-green-400 p-4 rounded font-mono text-sm h-64 overflow-y-auto">
                {configLog.length === 0 ? (
                  <p className="text-gray-500">No configuration run yet...</p>
                ) : (
                  configLog.map((log, i) => (
                    <p key={i} className="whitespace-pre-wrap">{log}</p>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Number Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-green-600 text-white">
              <Plus className="h-6 w-6" />
              <DialogTitle className="text-white">Add Inbound Number</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone Number *</Label>
                <Input
                  id="phone_number"
                  placeholder="+17144655921"
                  value={formData.phone_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">E.164 format (e.g., +17144655921)</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Display Name *</Label>
                <Input
                  id="name"
                  placeholder="Main Sales Line"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Description of this number's purpose"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>

            {/* Flags */}
            <div className="flex items-center gap-6 py-2 border-t border-b">
              <div className="flex items-center gap-2">
                <Switch
                  id="is_primary"
                  checked={formData.is_primary}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_primary: checked }))}
                />
                <Label htmlFor="is_primary">Primary Number</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="enable_advisor"
                  checked={formData.enable_advisor_tool}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, enable_advisor_tool: checked }))}
                />
                <Label htmlFor="enable_advisor">Advisor Tool</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="enable_context"
                  checked={formData.enable_caller_context_tool}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, enable_caller_context_tool: checked }))}
                />
                <Label htmlFor="enable_context">Caller Context</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="enable_email"
                  checked={formData.enable_email_tool}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, enable_email_tool: checked }))}
                />
                <Label htmlFor="enable_email">Email Tool</Label>
              </div>
            </div>

            {/* Voice Settings */}
            <div className="space-y-4">
              <VoiceSelector
                value={formData.voice}
                onChange={(value) => setFormData(prev => ({ ...prev, voice: value }))}
                label="Voice"
                showPreview={true}
              />
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select value={formData.model} onValueChange={(value) => setFormData(prev => ({ ...prev, model: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MODELS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select value={formData.language} onValueChange={(value) => setFormData(prev => ({ ...prev, language: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Max Duration (min)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={formData.max_duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_duration: parseInt(e.target.value) || 10 }))}
                  />
                </div>
              </div>
            </div>

            {/* Prompt Type */}
            <div className="space-y-2">
              <Label>Prompt Type</Label>
              <Select value={formData.prompt_type} onValueChange={(value) => setFormData(prev => ({ ...prev, prompt_type: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROMPT_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Transfer Config */}
            <div className="border-t pt-4">
              <h3 className="font-medium mb-4">Transfer Configuration</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Default Transfer</Label>
                  <Input
                    placeholder="+17145551234"
                    value={formData.transfer_default}
                    onChange={(e) => setFormData(prev => ({ ...prev, transfer_default: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pricing Specialist</Label>
                  <Input
                    placeholder="+17145551235"
                    value={formData.transfer_pricing}
                    onChange={(e) => setFormData(prev => ({ ...prev, transfer_pricing: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Technical Support</Label>
                  <Input
                    placeholder="+17145551236"
                    value={formData.transfer_technical}
                    onChange={(e) => setFormData(prev => ({ ...prev, transfer_technical: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddNumber}
              disabled={!formData.phone_number || !formData.name || createMutation.isPending}
            >
              {createMutation.isPending ? 'Adding...' : 'Add Number'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Number Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-blue-600 text-white">
              <Pencil className="h-6 w-6" />
              <DialogTitle className="text-white">Edit Inbound Number</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input value={formData.phone_number} disabled className="bg-muted" />
                <p className="text-xs text-muted-foreground">Phone number cannot be changed</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Display Name</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="flex items-center gap-6 py-2 border-t border-b">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_primary}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_primary: checked }))}
                />
                <Label>Primary</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.enable_advisor_tool}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, enable_advisor_tool: checked }))}
                />
                <Label>Advisor</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.enable_caller_context_tool}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, enable_caller_context_tool: checked }))}
                />
                <Label>Context</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.enable_email_tool}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, enable_email_tool: checked }))}
                />
                <Label>Email</Label>
              </div>
            </div>

            <div className="space-y-4">
              <VoiceSelector
                value={formData.voice}
                onChange={(value) => setFormData(prev => ({ ...prev, voice: value }))}
                label="Voice"
                showPreview={true}
              />
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select value={formData.model} onValueChange={(value) => setFormData(prev => ({ ...prev, model: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MODELS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select value={formData.language} onValueChange={(value) => setFormData(prev => ({ ...prev, language: value }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Max Duration</Label>
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={formData.max_duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_duration: parseInt(e.target.value) || 10 }))}
                  />
                </div>
              </div>
            </div>

            {/* Email Settings */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-muted-foreground mb-3">Email Settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>CC Emails</Label>
                  <Input
                    placeholder="cc1@company.com; cc2@company.com"
                    value={formData.email_cc}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_cc: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">Multiple emails separated by ; or ,</p>
                </div>
                <div className="space-y-2">
                  <Label>BCC Emails</Label>
                  <Input
                    placeholder="bcc1@company.com; bcc2@company.com"
                    value={formData.email_bcc}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_bcc: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">Multiple emails separated by ; or ,</p>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <Label>Transfer Notification Email (Default)</Label>
                <Input
                  placeholder="supervisor@company.com; sales@company.com"
                  value={formData.transfer_notify_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, transfer_notify_email: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                  Email to notify when a call is transferred (used when caller is not a known lead). Multiple emails separated by ; or ,
                </p>
              </div>

              {/* Business Hours Configuration */}
              <div className="mt-6 p-4 border rounded-lg bg-muted/30">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Business Hours for Human Transfers
                </h4>
                <p className="text-xs text-muted-foreground mb-4">
                  During business hours, AI agents can transfer calls to human specialists. Outside these hours, agents will offer callbacks instead.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Select
                      value={String(formData.business_hours_start)}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, business_hours_start: parseInt(value) }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[6, 7, 8, 9, 10, 11, 12].map(hour => (
                          <SelectItem key={hour} value={String(hour)}>
                            {hour === 12 ? '12:00 PM' : `${hour}:00 AM`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Select
                      value={String(formData.business_hours_end)}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, business_hours_end: parseInt(value) }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[12, 13, 14, 15, 16, 17, 18, 19, 20].map(hour => (
                          <SelectItem key={hour} value={String(hour)}>
                            {hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Select
                      value={formData.business_hours_timezone}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, business_hours_timezone: value }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/Los_Angeles">Pacific (PT)</SelectItem>
                        <SelectItem value="America/Denver">Mountain (MT)</SelectItem>
                        <SelectItem value="America/Chicago">Central (CT)</SelectItem>
                        <SelectItem value="America/New_York">Eastern (ET)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditNumber} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-purple-600 text-white">
              <Settings className="h-6 w-6" />
              <DialogTitle className="text-white">Number Settings</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Voice & Call Settings */}
            <VoiceSelector
              value={formData.voice}
              onChange={(value) => setFormData(prev => ({ ...prev, voice: value }))}
              label="Voice"
              showPreview={true}
            />
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Model</Label>
                <Select value={formData.model} onValueChange={(value) => setFormData(prev => ({ ...prev, model: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MODELS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={formData.language} onValueChange={(value) => setFormData(prev => ({ ...prev, language: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Max Duration (min)</Label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={formData.max_duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, max_duration: parseInt(e.target.value) || 10 }))}
                />
              </div>
            </div>

            {/* Prompt Type */}
            <div className="space-y-2">
              <Label>Prompt Type</Label>
              <Select value={formData.prompt_type} onValueChange={(value) => setFormData(prev => ({ ...prev, prompt_type: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROMPT_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Features */}
            <div className="flex items-center gap-6 py-2 border-t border-b">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.enable_advisor_tool}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, enable_advisor_tool: checked }))}
                />
                <Label>Advisor Tool</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.enable_caller_context_tool}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, enable_caller_context_tool: checked }))}
                />
                <Label>Caller Context</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.enable_email_tool}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, enable_email_tool: checked }))}
                />
                <Label>Email Tool</Label>
              </div>
            </div>

            {/* Transfer Config */}
            <div className="border-t pt-4">
              <h3 className="font-medium mb-4">Transfer Configuration</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Default Transfer</Label>
                  <Input
                    placeholder="+17145551234"
                    value={formData.transfer_default}
                    onChange={(e) => setFormData(prev => ({ ...prev, transfer_default: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pricing Specialist</Label>
                  <Input
                    placeholder="+17145551235"
                    value={formData.transfer_pricing}
                    onChange={(e) => setFormData(prev => ({ ...prev, transfer_pricing: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Technical Support</Label>
                  <Input
                    placeholder="+17145551236"
                    value={formData.transfer_technical}
                    onChange={(e) => setFormData(prev => ({ ...prev, transfer_technical: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Email Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CC Emails</Label>
                <Input
                  placeholder="cc1@company.com; cc2@company.com"
                  value={formData.email_cc}
                  onChange={(e) => setFormData(prev => ({ ...prev, email_cc: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">Multiple emails separated by ; or ,</p>
              </div>
              <div className="space-y-2">
                <Label>BCC Emails</Label>
                <Input
                  placeholder="bcc1@company.com; bcc2@company.com"
                  value={formData.email_bcc}
                  onChange={(e) => setFormData(prev => ({ ...prev, email_bcc: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">Multiple emails separated by ; or ,</p>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <Label>Transfer Notification Email (Default)</Label>
              <Input
                placeholder="supervisor@company.com; sales@company.com"
                value={formData.transfer_notify_email}
                onChange={(e) => setFormData(prev => ({ ...prev, transfer_notify_email: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Email to notify when a call is transferred (used when caller is not a known lead). Multiple emails separated by ; or ,
              </p>
            </div>

            {/* Business Hours Configuration */}
            <div className="mt-6 p-4 border rounded-lg bg-muted/30">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Business Hours for Human Transfers
              </h4>
              <p className="text-xs text-muted-foreground mb-4">
                During business hours, AI agents can transfer calls to human specialists. Outside these hours, agents will offer callbacks instead.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Select
                    value={String(formData.business_hours_start)}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, business_hours_start: parseInt(value) }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[6, 7, 8, 9, 10, 11, 12].map(hour => (
                        <SelectItem key={hour} value={String(hour)}>
                          {hour === 12 ? '12:00 PM' : `${hour}:00 AM`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Select
                    value={String(formData.business_hours_end)}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, business_hours_end: parseInt(value) }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[12, 13, 14, 15, 16, 17, 18, 19, 20].map(hour => (
                        <SelectItem key={hour} value={String(hour)}>
                          {hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select
                    value={formData.business_hours_timezone}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, business_hours_timezone: value }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/Los_Angeles">Pacific (PT)</SelectItem>
                      <SelectItem value="America/Denver">Mountain (MT)</SelectItem>
                      <SelectItem value="America/Chicago">Central (CT)</SelectItem>
                      <SelectItem value="America/New_York">Eastern (ET)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSettingsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditNumber} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Number Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-red-600 text-white">
              <Trash2 className="h-6 w-6" />
              <DialogTitle className="text-white">Deactivate Inbound Number</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <p>Are you sure you want to deactivate this inbound number?</p>
            {selectedNumber && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{formatPhoneNumber(selectedNumber.phone_number)}</p>
                {selectedNumber.name && (
                  <p className="text-sm text-muted-foreground">{selectedNumber.name}</p>
                )}
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              This will deactivate the number. It can be reactivated later.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteNumber}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bland Status Dialog */}
      <Dialog open={isBlandStatusDialogOpen} onOpenChange={setIsBlandStatusDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-purple-900 text-white">
              <Eye className="h-6 w-6" />
              <DialogTitle className="text-white">Bland.ai Configuration</DialogTitle>
            </div>
          </DialogHeader>

          <div className="bg-muted p-4 rounded">
            <pre className="whitespace-pre-wrap text-sm overflow-auto max-h-96">
              {blandStatusData ? JSON.stringify(blandStatusData, null, 2) : 'Loading...'}
            </pre>
          </div>

          <DialogFooter>
            <Button onClick={() => setIsBlandStatusDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default InboundNumbers
